import React, { useRef, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SetupPDFHeader, ExportType } from '../../SetupPDFComponents/SetupPDFHeader/SetupPDFHeader';
import { ISetupPdfMessages } from '../../../../types/setupPDFTypes';
import {
  useRouteParams,
  storySetupPdfQueryKeys,
  usePreviewStoryPdf,
  useExportStoryPdf,
  useFetchSettings,
  useFetchStory,
  fetchStoryContents
} from './hooks';
import { PageErrorBoundary } from '../../ErrorBoundary/PageErrorBoundary';
import { ViewName } from '../../../../instrumentation/views';
import { Box, CircularProgress, Typography } from '@mui/material';
import { styles } from './StorySetupPDFView.styles';
import { ProgressTrackerSettingsProvider } from '../../StoriesSettings/ProgressTrackerSettings/context/ProgressTracker.Provider';
import Toolbar, { Mode } from '../../SetupPDFComponents/PreviewToolbar/Toolbar';
import { StoryPreview } from './StoryPreview';
import { PdfEnhancementPanel } from './PdfEnhancementPanel';
import { IStoryMargins } from '@opengov/stories-shared/types';
import { routerActionCreators } from '@opengov/compose-app-router/state';

export type IStorySetupPdfProps = {
  entityId: number;
  entityName: string;
  entitySubdomain: string;
  messages: ISetupPdfMessages;
};
export type IExportPdfOptionsState = {
  displayTitle: boolean;
  displayDate: boolean;
  displayPageNumber: boolean;
  displayStartingPageNumber: boolean;
  error: string;
  enlargeFonts: boolean;
  startingPage: number;
  pdfPreviewLink: string;
  margins: IStoryMargins | null;
  storyMarginsEnabled: boolean;
  showHeader: boolean;
  showFooter: boolean;
  watermark: string | null;
};

export const StorySetupPDFViewInternal: React.FC<IStorySetupPdfProps> = ({ messages, entityId, entitySubdomain }) => {
  // Get route params from URL
  const iframeDocumentRef = useRef<Document | null>(null);
  const queryClient = useQueryClient();
  const { storyId } = useRouteParams();
  const [zoom, setZoom] = useState(100);
  const [storyMode, setStoryMode] = useState<Mode>('edit');
  const [activeElement, setActiveElement] = useState<HTMLElement | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const storyPreviewRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const [storyPreviewBaseWidth, setStoryPreviewBaseWidth] = useState<number>(0);

  // Data fetching hooks
  const { data: story, isLoading: isLoadingStory } = useFetchStory({ storyId });
  const { data: settings } = useFetchSettings({ entityId });

  const [exportPdfOptions, setExportPdfOptions] = useState<IExportPdfOptionsState>({
    displayTitle: false,
    displayDate: false,
    displayPageNumber: false,
    displayStartingPageNumber: false,
    error: '',
    enlargeFonts: false,
    startingPage: 1,
    pdfPreviewLink: '',
    margins: null,
    storyMarginsEnabled: true, // Must be true for custom margins to apply
    showHeader: true,
    showFooter: true,
    watermark: null
  });

  // PDF Preview - fire-and-forget pattern (same as Publication)
  const {
    mutate: generatePdfPreview,
    isLoading: isGeneratingPdf,
    cancelPreview
  } = usePreviewStoryPdf({
    onSuccess: (result) => {
      setPdfUrl(result.pdfUrl);
    }
  });

  // PDF Export hook
  const { exportPdf, isExporting } = useExportStoryPdf({
    onSuccess: () => {
      routerActionCreators.goBack();
    }
  });

  useEffect(() => {
    const container = containerRef.current;
    const storyPreview = storyPreviewRef.current;
    if (!container) return;
    if (!storyPreview) return;

    setContainerHeight(container.clientHeight);
    setStoryPreviewBaseWidth(storyPreview.offsetWidth);

  }, [storyMode, zoom, isLoadingStory]);

  const handlePreviewPdf = () => {
    generatePdfPreview({
      ...exportPdfOptions,
      startingPage: exportPdfOptions.startingPage.toString(),
      storyId,
      entitySubdomain
    });
  };

  const handleExport = async () => {
    if (!story) return;

    // Fetch story contents before export
    const storyContents = await fetchStoryContents({ storyId, entityId });
    if (!storyContents || storyContents.length === 0) return;

    await exportPdf({
      story,
      storyContents,
      settings,
      displayDate: exportPdfOptions.displayDate,
      displayPageNumber: exportPdfOptions.displayPageNumber,
      displayTitle: exportPdfOptions.displayTitle,
      enlargeFonts: exportPdfOptions.enlargeFonts,
      startingPage: exportPdfOptions.startingPage.toString(),
      margins: exportPdfOptions.margins || undefined,
      storyMarginsEnabled: exportPdfOptions.storyMarginsEnabled,
      showHeader: exportPdfOptions.showHeader,
      showFooter: exportPdfOptions.showFooter,
      watermark: exportPdfOptions.watermark,
      publication: story?.folder?.publication
    });
  };

  const handleDiscard = () => {
    routerActionCreators.goBack();
  };
  return (
    <>
      {isLoadingStory ? (
        <Box data-test="setupPDFStoryPreviewLoadingSpinner" sx={styles.loadingSpinner}>
          <CircularProgress />
          <Typography>{messages.loading}</Typography>
        </Box>
      ) : (
        <Box sx={styles.storySetupPDFViewContainer}>
          <SetupPDFHeader
            exportType={ExportType.STORY}
            title={messages.setupPdf}
            subtitle={story?.title}
            messages={messages}
            onExport={handleExport}
            onSaveAndExit={handleDiscard}
            onDiscard={handleDiscard}
            isApplyingStyles={isExporting}
            isExportDisabled={!story || isExporting || isLoadingStory}
          />
          <Box sx={styles.storyPDFContent}>
            <Box sx={styles.storyContent}>
              <Box sx={styles.toolbarContainer}>
                <Toolbar
                  messages={messages}
                  zoom={zoom}
                  currentStory={1}
                  totalStories={1}
                  selectedStoryId={storyId}
                  isCreatingPdf={false}
                  mode={storyMode}
                  onZoomIn={() => {
                    setZoom(zoom + 25);
                  }}
                  onZoomOut={() => {
                    setZoom(zoom - 25);
                  }}
                  onZoomChange={(val) => {
                    setZoom(val);
                  }}
                  onModeChange={(mode) => {
                    if (mode === 'preview') {
                      handlePreviewPdf();
                    }
                    if (mode === 'edit') {
                      // Cancel any pending PDF preview request and reset PDF URL
                      cancelPreview();
                      setPdfUrl(null);
                      // Reset query to clear cache completely and force fresh fetch with loading state
                      void queryClient.resetQueries({
                        queryKey: storySetupPdfQueryKeys.storyHtmlContent(storyId)
                      });
                    }
                    setStoryMode(mode);
                  }}
                />
              </Box>
              {storyMode === 'edit' ? (
                <Box ref={containerRef} sx={{ ...styles.storyPreviewContainer }}>
                  <Box sx={{
                    ...styles.storyPreviewWrapper,
                    height: containerHeight > 0 ? `${containerHeight * (zoom / 100)}px` : '100%',
                    width: storyPreviewBaseWidth > 0 ? `${storyPreviewBaseWidth * (zoom / 100)}px` : '100%'
                  }}>
                    <Box ref={storyPreviewRef} sx={{
                      ...styles.storyPreview,
                      height: containerHeight > 0 ? `${containerHeight}px` : '100%',
                      transform: `scale(${zoom / 100})`,
                    }}>
                      {isExporting ? (
                        <Box sx={styles.loadingSpinner}>
                          <CircularProgress />
                          <Typography>{messages.generatingPdf}</Typography>
                        </Box>
                      ) : (
                        <ProgressTrackerSettingsProvider entityId={entityId}>
                          <StoryPreview
                            iframeDocumentRef={iframeDocumentRef}
                            entityId={entityId}
                            storyId={storyId}
                            messages={messages}
                            activeElement={activeElement}
                            setActiveElement={setActiveElement}
                          />
                        </ProgressTrackerSettingsProvider>
                      )}
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box ref={containerRef} sx={{ ...styles.storyPreviewContainer }}>
                  <Box sx={{
                    ...styles.storyPreviewWrapper,
                    height: containerHeight > 0 ? `${containerHeight * (zoom / 100)}px` : '100%',
                    width: storyPreviewBaseWidth > 0 ? `${storyPreviewBaseWidth * (zoom / 100)}px` : '100%'
                  }}>
                    <Box ref={storyPreviewRef} sx={{
                      ...styles.storyPreview,
                      height: containerHeight > 0 ? `${containerHeight}px` : '100%',
                      transform: `scale(${zoom / 100})`,
                    }}>
                      {isGeneratingPdf || isExporting ? (
                        <Box sx={styles.loadingSpinner}>
                          <CircularProgress />
                          <Typography>{messages.generatingPdf}</Typography>
                        </Box>
                      ) : (
                        <iframe
                          src={`${pdfUrl}#toolbar=0`}
                          key={pdfUrl}
                          width="100%"
                          height="100%"
                          style={{ border: 'none' }}
                          title={messages.pdfPreview}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
            {storyMode === 'edit' ? (
              <PdfEnhancementPanel
                messages={messages}
                exportPdfOptions={exportPdfOptions}
                setExportPdfOptions={setExportPdfOptions}
              />
            ) : null}
          </Box>
        </Box>
      )}
    </>
  );
};

export const StorySetupPDFView = (props: IStorySetupPdfProps) => {
  return (
    <PageErrorBoundary viewName={ViewName.StorySetupPdfViewPage}>
      <StorySetupPDFViewInternal {...props} />
    </PageErrorBoundary>
  );
};
export default StorySetupPDFView;
